import React from "react";
import { useTranslation } from "react-i18next";
import { Reaction, ReactionSummary } from "types/Post";
import { REACTION_EMOJI, REACTION_NAME_KEYS } from "../utils/reactions";
import { TOUCH_TARGET, TOUCH_TARGET_PILL } from "./touchTargets";

interface ReactionPillsProps {
  reactions: ReactionSummary[];
  onToggle: (reaction: Reaction) => void;
  /** Pressing is off while an earlier press is still on its way. */
  isDisabled?: boolean;
  /** Smaller pills, for under a comment. */
  isSmall?: boolean;
}

/** One pill per reaction: the emoji and, once somebody has given it, how many did. The ones the viewer gave are highlighted. */
export function ReactionPills({ reactions, onToggle, isDisabled = false, isSmall = false }: ReactionPillsProps) {
  const { t } = useTranslation();
  const sizeClass = isSmall
    ? `px-1.5 py-0.5 text-xs max-md:py-1 ${TOUCH_TARGET_PILL}`
    : `px-2.5 py-1 text-sm ${TOUCH_TARGET}`;

  return (
    <div className="flex items-center gap-1.5">
      {reactions.map(({ reaction, count, reactedByMe }) => {
        const name = t(REACTION_NAME_KEYS[reaction]);
        return (
          <button
            key={reaction}
            type="button"
            aria-label={count > 0 ? `${name} ${count}` : name}
            title={name}
            aria-pressed={reactedByMe}
            disabled={isDisabled}
            onClick={() => onToggle(reaction)}
            className={`flex items-center gap-1 rounded-full border font-medium transition-colors disabled:opacity-60 ${sizeClass} ${
              reactedByMe
                ? "border-blue-300 bg-blue-50 text-blue-700"
                : "border-gray-200 bg-white text-gray-600 hover:bg-gray-100"
            }`}
          >
            <span aria-hidden="true">{REACTION_EMOJI[reaction]}</span>
            {count > 0 && <span aria-hidden="true">{count}</span>}
          </button>
        );
      })}
    </div>
  );
}
